import React, { useMemo, useState } from 'react';
import type { SiteEntity } from '../../types/site';
import type { StudyGeometryRole } from '../../types/siteStudyArea';
import { createSiteStudyArea, listStudyAreaCandidates, resolveSiteStudyArea, STUDY_GEOMETRY_ROLES, studyGeometryKey } from '../../lib/siteStudyArea';
import { useStore } from '../../store';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

/** Selection and roles are persisted; the map's visibility controls remain presentation only. */
export function SiteStudyAreaEditor({ site, onClose, onSavingChange }: { site: SiteEntity; onClose: () => void; onSavingChange?: (saving: boolean) => void }) {
  const candidates = useMemo(() => listStudyAreaCandidates(site), [site]);
  const [name, setName] = useState(site.studyArea?.name ?? 'DC + BTM development area');
  const [included, setIncluded] = useState(() => new Set(site.studyArea?.included.map(studyGeometryKey) ?? []));
  const [roles, setRoles] = useState<Record<string, StudyGeometryRole>>(() => Object.fromEntries(site.studyArea?.roles.map(row => [studyGeometryKey(row), row.role]) ?? []));
  const [filter, setFilter] = useState<'boundaries' | 'polygons' | 'all'>(() => candidates.some(row => row.kind === 'parcel') ? 'boundaries' : 'polygons');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const updateSite = useStore(state => state.updateSite);
  const [repairing, setRepairing] = useState<string | null>(null);

  /**
   * Applies a proposed single-vertex repair to the stored parcel.
   *
   * Only ever touches the one parcel's polygon — the selection, roles and every
   * other boundary are left exactly as they are. The repair was computed against
   * the same authority that rejected the polygon, so if this succeeds the parcel
   * becomes selectable; if the write fails the polygon is unchanged.
   */
  const applyRepair = async (row: ReturnType<typeof listStudyAreaCandidates>[number]) => {
    if (!row.repair || row.kind !== 'parcel') return;
    setRepairing(row.id);
    setError(null);
    try {
      const boundaries = (site.siteBoundaries ?? []).map(parcel => parcel.id === row.id
        ? { ...parcel, polygon: row.repair!.repaired }
        : parcel);
      await updateSite(site.id, { siteBoundaries: boundaries });
    } catch (caught) {
      setError(`Could not repair ${row.name}: ${(caught as Error).message}`);
    } finally {
      setRepairing(null);
    }
  };
  const preview = useMemo(() => {
    try {
      const studyArea = createSiteStudyArea(site, { name, includedKeys: [...included], roles, updatedAt: new Date().toISOString() });
      return { result: resolveSiteStudyArea({ ...site, studyArea }), error: null };
    } catch (caught) { return { result: null, error: (caught as Error).message }; }
  }, [site, name, included, roles]);
  const framing = included.size > 0 ? candidates.filter(row => included.has(studyGeometryKey(row)))
    : candidates.some(row => row.kind === 'parcel') ? candidates.filter(row => row.kind === 'parcel') : candidates;
  const points = framing.flatMap(row => row.polygon).filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  const { west, east, south, north } = points.reduce((bounds, point) => ({ west: Math.min(bounds.west, point.lng), east: Math.max(bounds.east, point.lng), south: Math.min(bounds.south, point.lat), north: Math.max(bounds.north, point.lat) }), { west: Infinity, east: -Infinity, south: Infinity, north: -Infinity });
  // A local equirectangular projection keeps polygon shape at the site's latitude.
  // One scale fits both axes; no independent longitude/latitude stretching.
  const centreLng = (west + east) / 2, centreLat = (south + north) / 2;
  const longitudeScale = Math.max(0.000001, Math.cos(centreLat * Math.PI / 180));
  const scale = Math.min(460 / Math.max((east - west) * longitudeScale, 0.000001), 200 / Math.max(north - south, 0.000001));
  const previewPoints = (polygon: typeof points) => polygon.filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng)).map(p => `${250 + (p.lng - centreLng) * longitudeScale * scale},${120 - (p.lat - centreLat) * scale}`).join(' ');
  const query = search.trim().toLocaleLowerCase();
  const visibleCandidates = candidates.filter(row => included.has(studyGeometryKey(row)) || (
    (filter === 'all' || (filter === 'boundaries' ? row.kind === 'parcel' : row.geometryType === 'polygon'))
    && (!query || `${row.name} ${row.id}`.toLocaleLowerCase().includes(query))
  )).sort((a, b) => Number(included.has(studyGeometryKey(b))) - Number(included.has(studyGeometryKey(a))) || Number(b.kind === 'parcel') - Number(a.kind === 'parcel'));
  const toggle = (key: string) => {
    const next = new Set(included);
    if (next.has(key)) next.delete(key);
    else {
      next.add(key);
      setRoles(current => ({ ...current, [key]: current[key] === 'property' ? 'property' : 'development' }));
    }
    setIncluded(next);
  };
  const save = async () => {
    setSaving(true); onSavingChange?.(true); setError(null);
    try {
      const studyArea = createSiteStudyArea(site, { name, includedKeys: [...included], roles, updatedAt: new Date().toISOString() });
      const current = useStore.getState().siteRag.sites.find(row => row.id === site.id) ?? site;
      if ((current.studyArea?.revision ?? 0) !== (site.studyArea?.revision ?? 0)) throw new Error('The study selection changed while this editor was open. Reopen it to review the latest selection.');
      resolveSiteStudyArea({ ...current, studyArea });
      // Neither `site` nor the store can vouch for a pin: updateSite re-applies the
      // caller's patch over the refreshed document, so a ring that was never written
      // survives every in-memory check (Pineywoods p2, 2026-09-15). Pins bind to
      // persisted geometry, so the document itself is read before the write.
      const { assertStudyAreaPinsMatchPersistedSite } = await import('../../lib/site/persistedStudyAreaPins');
      await assertStudyAreaPinsMatchPersistedSite(site, studyArea);
      await updateSite(site.id, { studyArea });
      onClose();
    } catch (caught) { setError((caught as Error).message); }
    finally { setSaving(false); onSavingChange?.(false); }
  };
  return <div className="flex min-h-0 max-h-[calc(100dvh-12rem)] flex-col gap-3" data-testid="site-study-area-editor">
    <div className="min-h-0 space-y-4 overflow-y-auto pr-1" data-testid="study-area-scroll-body">
    <p className="text-sm text-text-secondary">Choose the polygons included in this study. Other parcels and map layers remain available as context. Saving changes the area used by calculations and reports.</p>
    <label className="block text-sm">Study area name
      <input className="mt-1 w-full rounded border border-border bg-background p-2" value={name} maxLength={120} disabled={saving} onChange={event => setName(event.target.value)} />
    </label>
    {points.length > 0 && <svg viewBox="0 0 500 240" role="img" aria-label="Study polygon selection preview" className="w-full max-h-60 rounded border border-border bg-muted/20">
      {candidates.filter(row => row.geometryType === 'polygon').map(row => <polygon key={studyGeometryKey(row)} data-study-geometry={studyGeometryKey(row)} points={previewPoints(row.polygon)} fill={included.has(studyGeometryKey(row)) ? '#2563eb33' : 'transparent'} stroke={included.has(studyGeometryKey(row)) ? '#2563eb' : '#94a3b8'} strokeWidth={included.has(studyGeometryKey(row)) ? 3 : 1} onClick={() => !saving && row.geometryHash && toggle(studyGeometryKey(row))}>
        <title>{row.name}{included.has(studyGeometryKey(row)) ? ' — included' : ''}</title>
      </polygon>)}
    </svg>}
    <p className="text-xs text-text-secondary">Blue outlines are included. Acreage is gross polygon area; reference corridors do not establish legal setbacks or usable land.</p>
    <div className="flex flex-wrap items-end gap-2">
      <label className="min-w-48 flex-1 text-sm">Search features
        <input type="search" className="mt-1 w-full rounded border border-border bg-background p-2" value={search} onChange={event => setSearch(event.target.value)} placeholder="Name or layer ID" />
      </label>
      <label className="text-sm">Show features
        <select className="mt-1 block rounded border border-border bg-background p-2" value={filter} onChange={event => setFilter(event.target.value as typeof filter)}>
          {candidates.some(row => row.kind === 'parcel') && <option value="boundaries">Boundary polygons</option>}
          <option value="polygons">All polygons</option>
          <option value="all">All features and roles</option>
        </select>
      </label>
    </div>
    <p className="text-xs text-text-secondary" role="status">Showing {visibleCandidates.length} of {candidates.length} features. Selected polygons remain visible. Choose all features to edit point and line roles.</p>
    <div className="space-y-2">
      {visibleCandidates.map(row => {
        const key = studyGeometryKey(row);
        return <div key={key} className="flex flex-wrap items-center gap-2 rounded border border-border p-2">
          <label className="flex min-w-48 flex-1 items-center gap-2 text-sm">
            <input type="checkbox" aria-label={`Include ${row.name} in study`} checked={included.has(key)} disabled={saving || !row.geometryHash} onChange={() => toggle(key)} />
            <span>{row.name}<span className="block text-xs text-text-secondary">{row.kind === 'parcel' ? 'Boundary polygon' : `Map ${row.geometryType}`} · {row.id}{row.areaAcres != null ? ` · ${row.areaAcres.toFixed(2)} ac` : ''}{row.error ? ' · Cannot be selected' : ''}</span>
            {row.error && <span className="mt-1 block text-xs text-amber-700" data-testid={`study-area-error-${row.id}`}>{row.error}</span>}
            {row.repair && <span className="mt-1 block text-xs text-text-secondary" data-testid={`study-area-repair-${row.id}`}>
              One stray vertex explains it: point {row.repair.index + 1} sits {row.repair.spurMeters.toFixed(0)} m from its neighbour, while the shortest real edge is {row.repair.nextShortestEdgeMeters.toFixed(0)} m. Removing it changes the enclosed area by {(Math.abs(row.repair.areaChangeFraction) * 100).toFixed(2)}%.
            </span>}
            </span>
          </label>
          {row.repair && <button
            type="button"
            data-testid={`study-area-repair-action-${row.id}`}
            disabled={saving || repairing === row.id}
            className="rounded border border-border px-2 py-1 text-xs font-semibold text-primary disabled:opacity-50"
            onClick={() => void applyRepair(row)}
          >{repairing === row.id ? 'Repairing…' : 'Remove stray vertex'}</button>}
          <select aria-label={`Role for ${row.name}`} value={roles[key] ?? 'reference'} disabled={saving} className="max-w-full rounded border border-border bg-background p-1 text-xs" onChange={event => {
            const role = event.target.value as StudyGeometryRole;
            setRoles(current => ({ ...current, [key]: role }));
            if (role === 'reference' || role === 'corridor') setIncluded(current => { const next = new Set(current); next.delete(key); return next; });
          }}>
            {STUDY_GEOMETRY_ROLES.map(role => <option key={role.value} value={role.value}>{role.label}</option>)}
          </select>
        </div>;
      })}
    </div>
    {visibleCandidates.length === 0 && <p className="text-sm text-text-secondary">No features match this search and filter.</p>}
    <p className="text-xs text-text-secondary">Editing a selected outline later requires reviewing this selection again. Existing reports retain their original scope and become stale when the study basis changes.</p>
    </div>
    <div className="shrink-0 space-y-2 border-t border-border bg-background pt-3" data-testid="study-area-footer">
      {preview.result && <p className="text-sm font-medium" data-testid="study-area-acreage">{preview.result.scope.grossAreaAcres.toFixed(2)} gross acres · {included.size} selected polygon{included.size === 1 ? '' : 's'} · overlaps counted once</p>}
      {(error || preview.error) && <p role={error ? 'alert' : 'status'} className="text-sm text-amber-700">{error ?? preview.error}</p>}
      <div className="flex justify-end gap-2"><Button variant="outline" disabled={saving} onClick={onClose}>Cancel</Button><Button disabled={saving || !preview.result} onClick={() => void save()}>{saving ? 'Saving…' : 'Save study area'}</Button></div>
    </div>
  </div>;
}

export function SiteStudyAreaDialog({ site, onClose }: { site: SiteEntity; onClose: () => void }) {
  const [saving, setSaving] = useState(false);
  return <Dialog open onOpenChange={open => { if (!open && !saving) onClose(); }}>
    <DialogContent size="lg" className="z-[65] flex max-h-[92dvh] flex-col overflow-hidden" overlayClassName="z-[64]" showCloseButton={!saving}>
      <DialogHeader className="shrink-0">
        <DialogTitle>Define study area</DialogTitle>
        <DialogDescription className="sr-only">Select study polygons and assign map feature roles.</DialogDescription>
      </DialogHeader>
      <SiteStudyAreaEditor site={site} onClose={onClose} onSavingChange={setSaving} />
    </DialogContent>
  </Dialog>;
}
